import type { Workspace } from '@/generated/workspace.types'
import { captureError, type CaptureContext } from './errorCapture'

type ZiggyConfig = { defaults?: Record<string, unknown> }

/**
 * Shared Inertia props this sync reads. `currentWorkspace` is the primary
 * source (see the docblock on `syncZiggyWebsiteDefault`); `currentUser` is only
 * used to tell an authenticated render apart from `/login` so a missing hashid
 * is reported instead of silently ignored. `user.current_site` is the legacy
 * fallback for the rare render that still lacks the PageContext props.
 */
type SyncProps = {
    currentUser?: unknown
    currentWorkspace?: Pick<Workspace, 'hashid'> | null
    user?: {
        current_site?: {
            hashid?: string | null
        } | null
    } | null
}

type PageWithProps = { props?: Record<string, unknown> }

/**
 * The `@routes` Blade directive emits `const Ziggy = {...}` as a *global lexical*
 * binding — NOT `window.Ziggy`. `route()` (both the `ziggy-js` import and the
 * `ZiggyVue` global) resolves its config as `typeof Ziggy !== 'undefined' ? Ziggy
 * : globalThis.Ziggy`, so the lexical `Ziggy` is the object every route link
 * actually reads.
 */
declare const Ziggy: ZiggyConfig | undefined

/** Reads the global *lexical* `Ziggy` that `@routes` emits and `route()` reads. */
function readLexicalZiggy(): ZiggyConfig | undefined {
    return typeof Ziggy !== 'undefined' ? Ziggy : undefined
}

/** Fallback: the `globalThis.Ziggy` object (also the seam tests inject). */
function readGlobalZiggy(): ZiggyConfig | undefined {
    return (globalThis as unknown as { Ziggy?: ZiggyConfig }).Ziggy
}

/**
 * Resolves the Ziggy config `route()` reads, mirroring ziggy-js's own order:
 * the lexical `Ziggy` first, `globalThis.Ziggy` as fallback.
 *
 * The readers are injectable so the resolution *order* is unit-testable. The
 * real lexical `const Ziggy` binding can't be reproduced from module code in
 * vitest — a module can't create a global lexical `const`, and setting
 * `globalThis.Ziggy` also flips `typeof Ziggy !== 'undefined'` to true, so the
 * two branches are indistinguishable through the globals alone. Injecting the
 * readers verifies the precedence explicitly instead.
 */
export function resolveZiggyConfig(
    readLexical: () => ZiggyConfig | undefined = readLexicalZiggy,
    readGlobal: () => ZiggyConfig | undefined = readGlobalZiggy
): ZiggyConfig | undefined {
    return readLexical() ?? readGlobal()
}

/**
 * Keeps Ziggy's `website` route default in sync with the current workspace.
 *
 * The server only injects the `website` default (via `URL::defaults` in
 * `SetDefaultTeamForUrlsMiddleware`) on full page loads that pass through that
 * middleware. Inertia SPA visits never re-run `@routes`, and some authenticated
 * renders (session-expiry/login-adjacent, error pages) reach the persistent
 * chrome without the middleware having run — leaving `Ziggy.defaults` without
 * `website`. Every `route('inbox')`-style call in the chrome (MainMenu, Sidebar,
 * BannerMessages) then throws "'website' parameter is required".
 *
 * The hashid is sourced from `currentWorkspace.hashid`, which `PageContext`
 * builds from `auth()->user()->current_website_id` (WorkspaceService) and shares
 * on *every* authenticated response — independent of the resolver singleton that
 * `SetDefaultTeamForUrlsMiddleware` and the server-side `URL::defaults` both hang
 * off. That is what lets this fix hold on the full-load / middleware-absent case
 * the previous `user.current_site.hashid` source (produced by the same gated
 * code path as the server default) could never reach. `user.current_site.hashid`
 * is kept only as a fallback.
 *
 * The mechanism fails silently — that is how #1791 shipped a no-op that survived
 * weeks — so a render that should have a hashid or a config but doesn't reports
 * through `captureError`, while genuinely-unauthenticated renders (`/login`,
 * no `currentUser`) stay quiet.
 */
export function syncZiggyWebsiteDefault(
    page: PageWithProps | undefined,
    ziggy: ZiggyConfig | undefined = resolveZiggyConfig(),
    report: (error: unknown, context?: CaptureContext) => void = captureError
): void {
    const props = page?.props as SyncProps | undefined
    const hashid =
        props?.currentWorkspace?.hashid ?? props?.user?.current_site?.hashid

    if (!hashid) {
        if (props?.currentUser) {
            report(
                new Error(
                    'Ziggy website default: workspace hashid missing on an authenticated render'
                ),
                { source: 'ziggy-sync', reason: 'missing-hashid' }
            )
        }
        return
    }

    if (!ziggy) {
        report(
            new Error(
                'Ziggy website default: config unresolved — route() links will throw "\'website\' parameter is required"'
            ),
            { source: 'ziggy-sync', reason: 'unresolved-config' }
        )
        return
    }

    ziggy.defaults = { ...ziggy.defaults, website: hashid }
}
